import type { CategoryViewProps, GameMinimalProps } from '../../types';
import { CardTypeEnum } from '../../types/enum';

import React from 'react';
import dynamic from 'next/dynamic';
import styled from '@emotion/styled';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';
import { useAppContext } from '../../contexts/AppContext';
import { Card, CardSkeleton } from '../../components/Card';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { useTheme } from '@mui/material/styles';
import { Title } from '../../components/Title';
import { CollectionSection } from '../../components/CollectionSection';
import { useCursorContext } from '../../contexts/CursorContext';

const CarouselList = dynamic<any>(() => import('../../components/CarouselList').then((mod) => mod.CarouselList), {
  ssr: false,
});

const CardSkeletonStyled = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '16/9',
}));

export const CollectionD = (props: CategoryViewProps) => {
  const { title, collection, global, pageContext } = props;
  const { ref, inView } = useInView();
  const { t } = useTranslation('collection_d');
  const theme = useTheme();
  const appContext = useAppContext();
  const { enabled } = useCursorContext();
  const { assetUrl, componentOptions } = appContext.useConfig();

  const emptyItems = 0;
  const cardType = componentOptions?.component_game_carousel_card_type;

  const breaks = {
    xs: 6,
    sm: 4,
    md: 3,
    lg: 3,
  };

  const swiperOptions = {
    breakpoints: {
      900: {
        slidesPerView: 4,
        slidesPerGroup: 4,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
      },
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
      },
    },
  };

  return (
    <Container maxWidth={false}>
      <BreadCrumbs breadcrumbs={pageContext.breadcrumbs} hidden={componentOptions?.component_breadcrumb_hidden} />
      <CollectionSection>
        {title && (
          <Title component="h1" variant="h1" disableGutters sx={{ paddingLeft: theme.spacing(2) }}>
            {title}
          </Title>
        )}
        <Grid container spacing={2} sx={{ padding: theme.spacing(2) }}>
          {collection.games.map((game: GameMinimalProps, index: number) => (
            <Grid key={game.id || index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
              <Card
                key={game.name}
                href={`${enabled ? '#' : `${game.slug}`}`}
                src={`${assetUrl}${game.images.cover16x9 || game.images.cover}/`}
                alt={game.name || 'Cover'}
                title={game.name || undefined}
                description={game.description || undefined}
                width={componentOptions.component_card_width || 512}
                height={componentOptions.component_card_height || 288}
                template={CardTypeEnum.SIMPLE}
                sizes={`${theme.breakpoints.up('md').replace('@media ', '')} 25vw, ${theme.breakpoints
                  .up('sm')
                  .replace('@media ', '')} 33.333vw, 50vw`}
                layout="responsive"
                hoverEffect={componentOptions?.component_card_hover_effect || false}
                priority={index === 0}
                typographyOptions={{
                  align: 'center',
                }}
                // hoverEffect={true}
              />
            </Grid>
          ))}
          {[...Array(emptyItems)].map((item, index) => (
            <Grid key={index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
              <CardSkeletonStyled elevation={1} />
            </Grid>
          ))}
        </Grid>
      </CollectionSection>
      {inView && Array.isArray(global.popular) && global.popular.length > 0 && (
        <CollectionSection style={{ marginTop: '36px' }}>
          <Title component="h2" variant="h2">
            {t('popular_header', 'Popular')}
          </Title>
          <CarouselList
            padding={theme.spacing(2)}
            items={global.popular || []}
            cardOptions={{
              width: componentOptions.component_card_width || 512,
              height: componentOptions.component_card_height || 288,
              sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
              layout: 'responsive',
              template: 'extensive',
            }}
            {...swiperOptions}
          />
        </CollectionSection> )}
    </Container>
  );
};
